// (C) Copyright 2025 Hewlett Packard Enterprise Development LP

import { Box, Notification } from 'grommet'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import lodashGet from 'lodash/get'

import { ObjectList, Typography } from '../../../../../../components'
import { WizardContext } from '../../../../../../components/wizard/WizardContext'
import { renameKeys } from '../../../../../../utils/common-utils'
import { getAuthentication, idp } from '../../../constants'

const ReviewAndCreate = () => {
  const { formValues } = useContext(WizardContext)
  const { t } = useTranslation(['iam'])

  const tempI18NKeys = {
    general: {
      connectionName: t(
        'iam:sso_configurations.view_edit_create_connection.connection_name'
      ),
      authentication: t(
        'iam:sso_configurations.view_edit_create_connection.authentication'
      )
    },
    samlAttributes: {
      email: t('iam:sso_profiles.view_edit_create.email_address'),
      first_name: t('iam:sso_profiles.view_edit_create.first_name'),
      last_name: t('iam:sso_profiles.view_edit_create.last_name'),
      ccs_attribute: t('iam:sso_profiles.view_edit_create.ccs_attribute')
    },
    identityProviderConfiguration: {
      entityId: t('iam:sso_profiles.view_edit_create.greenlake_entity_id'),
      acsUrl: t(
        'iam:sso_configurations.view_edit_create_connection.destination_url'
      ),
      defaultRelayState: t(
        'iam:sso_profiles.view_edit_create.default_relay_state'
      )
    },
    greenLakeConfiguration: {
      entity_id: t('iam:sso_profiles.view_edit_create.idp_entity_id'),
      login_url: t('iam:sso_profiles.view_edit_create.login_url'),
      logout_url: t('iam:sso_profiles.view_edit_create.logout_url'),
      signing_certificate: t('iam:sso_profiles.view_edit_create.certificate')
    },
    sessionTimeout: {
      sessionTimeout: t('iam:sso_profiles.view_edit_create.session_timeout')
    }
  }

  const getGeneralObject = () => {
    return {
      connectionName: lodashGet(formValues, 'general.connectionName'),
      authentication: getAuthentication('Saml')
    }
  }

  const getSamlAttributesObject = () => {
    return {
      email: lodashGet(formValues, 'attribute_mapping.email'),
      first_name: lodashGet(formValues, 'attribute_mapping.firstName'),
      last_name: lodashGet(formValues, 'attribute_mapping.lastName'),
      ccs_attribute: 'HPE_GreenLake_authorization'
    }
  }

  const getIdentityProviderConfigurationObject = () => {
    return {
      entityId: idp.entityIdUrl,
      acsUrl: idp.acsUrl,
      defaultRelayState: idp.defaultRelayStateUrl
    }
  }

  const getGreenLakeConfigurationObject = () => {
    return {
      entity_id: lodashGet(formValues, 'saml_idp_config.entity_id'),
      login_url: lodashGet(formValues, 'saml_idp_config.login_url'),
      logout_url: lodashGet(formValues, 'saml_idp_config.logout_url'),
      signing_certificate: lodashGet(
        formValues,
        'saml_idp_config.signing_certificate'
      )
    }
  }

  const getSessionTimeoutObject = () => {
    return {
      sessionTimeout: lodashGet(formValues, 'timeout')
    }
  }

  const getFilteredObject = (obj, filteredValues = []) => {
    const filteredObjectValues = {}
    Object.entries(obj).forEach(([key, value]) => {
      if (filteredValues.includes(key)) {
        filteredObjectValues.id = { ...filteredObjectValues.id, [key]: value }
      }
    })
    return filteredObjectValues.id
  }

  const general = renameKeys(
    tempI18NKeys.general,

    getFilteredObject(getGeneralObject(), Object.keys(tempI18NKeys.general))
  )

  const samlAttributes = renameKeys(
    tempI18NKeys.samlAttributes,

    getFilteredObject(
      getSamlAttributesObject(),
      Object.keys(tempI18NKeys.samlAttributes)
    )
  )

  const identityProviderConfiguration = renameKeys(
    tempI18NKeys.identityProviderConfiguration,

    getFilteredObject(
      getIdentityProviderConfigurationObject(),
      Object.keys(tempI18NKeys.identityProviderConfiguration)
    )
  )

  const greenLakeConfiguration = renameKeys(
    tempI18NKeys.greenLakeConfiguration,
    getFilteredObject(
      getGreenLakeConfigurationObject(),
      Object.keys(tempI18NKeys.greenLakeConfiguration)
    )
  )

  const sessionTimeout = renameKeys(
    tempI18NKeys.sessionTimeout,
    getFilteredObject(
      getSessionTimeoutObject(),
      Object.keys(tempI18NKeys.sessionTimeout)
    )
  )

  return (
    <Box gap="medium" pad={{ bottom: 'medium' }}>
      <ObjectList
        data={general}
        testId="review-general"
        title={t('iam:sso_profiles.view_edit_create.general')}
        showRuler={false}
      />
      <ObjectList
        data={samlAttributes}
        testId="review-saml-attributes"
        title={t('iam:sso_profiles.view_edit_create.saml_attributes')}
        showRuler={false}
      />
      <ObjectList
        data={identityProviderConfiguration}
        testId="review-identity-provider-configuration"
        title={t(
          'iam:sso_profiles.view_edit_create.identity_provider_configuration'
        )}
        showRuler={false}
      />
      <ObjectList
        data={greenLakeConfiguration}
        testId="review-greenlake-configuration"
        title={t('iam:sso_profiles.view_edit_create.greenlake_configuration')}
        showRuler={false}
      />
      <ObjectList
        data={sessionTimeout}
        testId="review-session-timeout"
        title={t('iam:sso_profiles.view_edit_create.session_timeout')}
        showRuler={false}
      />
      <Box pad={{ bottom: 'small' }}>
        <Notification
          status="info"
          type="inline"
          message={
            <Typography type="text">
              {t(
                'iam:sso_configurations.view_edit_create_connection.create_connection_review_info'
              )}
            </Typography>
          }
        />
      </Box>
    </Box>
  )
}

export default ReviewAndCreate
